import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { ObserverLocation, PredictedPass } from "./prediction";
import type { SatelliteTleData } from "../SatDataLoader/types";

export type StartSimulationInput = {
  pass: PredictedPass;
  satellite: SatelliteTleData;
  observer: ObserverLocation;
  minElevation: number;
  timeZone: string;
};

export type SimulationSession = StartSimulationInput & {
  id: number;
  startTimeUTCms: number;
  endTimeUTCms: number;
};

type SimulationControls = {
  session: SimulationSession | null;
  playing: boolean;
  speed: number;
  error: string | null;
  startSimulation: (input: StartSimulationInput) => void;
  play: () => void;
  pause: () => void;
  seek: (timestamp: number) => void;
  setSpeed: (speed: number) => void;
  returnLive: () => void;
  fail: (message: string) => void;
};

type PlaybackClock = {
  session: SimulationSession | null;
  anchorTime: number;
  anchorWall: number;
  playing: boolean;
  speed: number;
  error: string | null;
};

const DEFAULT_SPEED = 60;

const ControlsContext = createContext<SimulationControls | null>(null);
const TimeContext = createContext<number | null>(null);

export const PassSimulationProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [session, setSession] = useState<SimulationSession | null>(null);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeedState] = useState(DEFAULT_SPEED);
  const [error, setError] = useState<string | null>(null);
  const [timestamp, setTimestamp] = useState<number | null>(null);
  const nextId = useRef(0);
  const clock = useRef<PlaybackClock>({
    session: null,
    anchorTime: 0,
    anchorWall: 0,
    playing: false,
    speed: DEFAULT_SPEED,
    error: null,
  });

  const currentTime = useCallback(() => {
    const current = clock.current;
    if (!current.session) return null;
    const elapsed = current.playing
      ? (performance.now() - current.anchorWall) * current.speed
      : 0;
    return Math.max(
      current.session.startTimeUTCms,
      Math.min(current.session.endTimeUTCms, current.anchorTime + elapsed),
    );
  }, []);

  const pause = useCallback(() => {
    const now = currentTime();
    if (now === null) return;
    clock.current.anchorTime = now;
    clock.current.anchorWall = performance.now();
    clock.current.playing = false;
    setTimestamp(now);
    setPlaying(false);
  }, [currentTime]);

  const startSimulation = useCallback(
    (input: StartSimulationInput) => {
      const { pass, satellite, observer } = input;
      const startTimeUTCms = Math.max(
        pass.riseTime - 300000,
        pass.tleEpoch - 86400000,
      );
      const endTimeUTCms = Math.min(
        pass.setTime,
        pass.tleEpoch + 14 * 86400000,
      );
      if (
        ![
          startTimeUTCms,
          endTimeUTCms,
          observer.lat,
          observer.lng,
          observer.heightMeters,
          input.minElevation,
        ].every(Number.isFinite) ||
        endTimeUTCms <= startTimeUTCms ||
        satellite.info.satid !== pass.satId
      ) {
        pause();
        const message =
          "This pass cannot be simulated. Find passes again with current orbital data.";
        clock.current.error = message;
        setError(message);
        return;
      }
      const next: SimulationSession = {
        ...input,
        id: ++nextId.current,
        pass: {
          ...pass,
          opticalWindows: pass.opticalWindows.map((window) => ({ ...window })),
        },
        satellite: { ...satellite, info: { ...satellite.info } },
        observer: { ...observer },
        startTimeUTCms,
        endTimeUTCms,
      };
      const shouldPlay = !document.hidden;
      clock.current = {
        session: next,
        anchorTime: startTimeUTCms,
        anchorWall: performance.now(),
        playing: shouldPlay,
        speed: DEFAULT_SPEED,
        error: null,
      };
      setSession(next);
      setTimestamp(startTimeUTCms);
      setSpeedState(DEFAULT_SPEED);
      setError(null);
      setPlaying(shouldPlay);
    },
    [pause],
  );

  const play = useCallback(() => {
    const current = clock.current;
    if (!current.session || current.error || document.hidden) return;
    const now = currentTime() ?? current.session.startTimeUTCms;
    current.anchorTime =
      now >= current.session.endTimeUTCms
        ? current.session.startTimeUTCms
        : now;
    current.anchorWall = performance.now();
    current.playing = true;
    setTimestamp(current.anchorTime);
    setPlaying(true);
  }, [currentTime]);

  const seek = useCallback((nextTime: number) => {
    const current = clock.current;
    if (!current.session || current.error || !Number.isFinite(nextTime)) return;
    current.anchorTime = Math.max(
      current.session.startTimeUTCms,
      Math.min(current.session.endTimeUTCms, nextTime),
    );
    current.anchorWall = performance.now();
    current.playing = false;
    setTimestamp(current.anchorTime);
    setPlaying(false);
  }, []);

  const setSpeed = useCallback(
    (nextSpeed: number) => {
      if (![1, 10, 30, 60].includes(nextSpeed)) return;
      const now = currentTime();
      if (now !== null) {
        clock.current.anchorTime = now;
        clock.current.anchorWall = performance.now();
        setTimestamp(now);
      }
      clock.current.speed = nextSpeed;
      setSpeedState(nextSpeed);
    },
    [currentTime],
  );

  const returnLive = useCallback(() => {
    clock.current = {
      session: null,
      anchorTime: 0,
      anchorWall: 0,
      playing: false,
      speed: DEFAULT_SPEED,
      error: null,
    };
    setSession(null);
    setTimestamp(null);
    setPlaying(false);
    setSpeedState(DEFAULT_SPEED);
    setError(null);
  }, []);

  const fail = useCallback(
    (message: string) => {
      if (!clock.current.session || clock.current.error) return;
      pause();
      clock.current.error = message;
      setError(message);
    },
    [pause],
  );

  useEffect(() => {
    if (!session || !playing) return;
    const interval = window.setInterval(() => {
      if (document.hidden) {
        pause();
        return;
      }
      const now = currentTime();
      if (now === null) return;
      setTimestamp(now);
      if (now >= session.endTimeUTCms) pause();
    }, 100);
    return () => window.clearInterval(interval);
  }, [session, playing, currentTime, pause]);

  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.hidden) pause();
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", onVisibilityChange);
  }, [pause]);

  const controls = useMemo(
    () => ({
      session,
      playing,
      speed,
      error,
      startSimulation,
      play,
      pause,
      seek,
      setSpeed,
      returnLive,
      fail,
    }),
    [
      session,
      playing,
      speed,
      error,
      startSimulation,
      play,
      pause,
      seek,
      setSpeed,
      returnLive,
      fail,
    ],
  );

  return (
    <ControlsContext.Provider value={controls}>
      <TimeContext.Provider value={timestamp}>{children}</TimeContext.Provider>
    </ControlsContext.Provider>
  );
};

export const usePassSimulation = () => {
  const context = useContext(ControlsContext);
  if (!context) throw new Error("PassSimulationProvider is required");
  return context;
};

export const useSimulationTime = () => useContext(TimeContext);
